import { useCallback, useEffect } from 'react';
import { IPwaInstallPromptEvent } from './pwa.t';
import { useDispatch, useSelector } from 'react-redux';
import { pwaSlice } from './pwaReducer';
import { logAction, logAppsflyerAction } from '../../app/appActions';
import { LOCAL_STORAGE_KEYS, LOG_AF_EVENT_NAMES } from '../../app/appConstants';
import moment from 'moment';
import { getPwaStateSelector } from './pwaSelectors';
import { getAppModeSelector } from '../../app/appSelectors';

function PwaProvider() {
    const dispatch = useDispatch();
    const pwaState = useSelector(getPwaStateSelector);
    const appMode = useSelector(getAppModeSelector);

    const beforeInstallPromptHandler = useCallback((e: IPwaInstallPromptEvent) => {
        e.preventDefault();
        dispatch(pwaSlice.actions.setInstallPromptEventAction(e));
    }, [dispatch]);

    const appInstalledHandler = useCallback(() => {
        dispatch(logAppsflyerAction({
            name: LOG_AF_EVENT_NAMES.PWA_INSTALL,
            payload: { referer: window.location.href },
        }));
        dispatch(logAction({ operation: 'pwa_install', referer: window.location.href }));
    }, [dispatch]);

    // listen PWA events
    useEffect(() => {
        window.addEventListener('beforeinstallprompt', beforeInstallPromptHandler);
        window.addEventListener('appinstalled', appInstalledHandler);

        return () => {
            window.removeEventListener('beforeinstallprompt', beforeInstallPromptHandler);
            window.removeEventListener('appinstalled', appInstalledHandler);
        }
    }, [appInstalledHandler, beforeInstallPromptHandler]);

    // check if PWA opened
    useEffect(() => {
        const isPwaMode = appMode === 'pwa';
        if (isPwaMode) {
            const pwaOpenedAt = localStorage.getItem(LOCAL_STORAGE_KEYS.PWA_OPENED_AT);
            localStorage.setItem(LOCAL_STORAGE_KEYS.PWA_OPENED_AT, moment().toISOString());

            const eventName = pwaOpenedAt
                ? LOG_AF_EVENT_NAMES.PWA_OPEN
                : LOG_AF_EVENT_NAMES.PWA_OPEN_FIRST;
            dispatch(logAppsflyerAction({
                name: eventName,
                payload: { referer: window.location.href },
            }));
            dispatch(logAction({
                operation: eventName,
                referer: window.location.href,
            }));
        }
        dispatch(pwaSlice.actions.initPwaAction({ isPwaMode }));
    }, [dispatch, appMode]);

    // init web push notifications
    useEffect(() => {
        if(pwaState.isPwaMode && pwaState.isInited) {
            dispatch(pwaSlice.actions.initPwaWebPushNotificationsAction());
        }
    }, [dispatch, pwaState.isInited, pwaState.isPwaMode]);

    return null;
}

export default PwaProvider;
